/**
 * A client account in the record panel: who they are, where they can sign
 * in, and each membership as a row into that organization's record.
 */
import { useQuery } from '@tanstack/react-query';

import { getAccount } from '@optum/api-client/endpoints/accounts';

import { type RecordRef, useRecordPanel } from '../../components/shell/record.js';
import {
  RecordActions,
  RecordAsync,
  RecordColumn,
  RecordFacts,
  RecordIdentity,
  RecordOpen,
  RecordRelated,
  RecordSection,
} from '../../components/shell/RecordParts.js';
import { absoluteDate, relativeTime, shortId } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { Avatar, Typography } from '../../ui/finixt.js';
import { StatusBadge } from '../../ui/StatusBadge.js';

function initialsOf(email: string): string {
  const name = email.split('@')[0] ?? email;
  const parts = name.split(/[._-]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? name[0] ?? '?';
  const second = parts[1]?.[0] ?? '';
  return `${first}${second}`.toUpperCase();
}

export function AccountRecord({ record }: { record: RecordRef }) {
  const panel = useRecordPanel();
  const sub = record.id;
  const account = useQuery({ queryKey: qk.account(sub), queryFn: () => getAccount(sub) });

  return (
    <RecordAsync query={account} record={record}>
      {(a) => (
        <RecordColumn>
          <RecordIdentity
            avatar={
              <Avatar
                initials={initialsOf(a.email)}
                size={40}
                shape="round"
                tint="var(--violet-tint)"
                color="var(--violet)"
              />
            }
            eyebrow={
              <>
                <Typography variant="eyebrow">Account</Typography>
                <StatusBadge status={a.status} />
              </>
            }
            name={a.email}
            subtitle={`${a.idp_name} · ${a.memberships.length} organization${a.memberships.length === 1 ? '' : 's'}`}
          />
          <RecordSection label="Facts">
            <RecordFacts
              items={[
                { label: 'Subject', value: shortId(a.external_sub) },
                { label: 'User', value: shortId(a.platform_user_id) },
                { label: 'User status', value: a.user_status, mono: false },
                { label: 'Created', value: absoluteDate(a.created_at) },
                {
                  label: 'Last login',
                  value: a.last_login_at ? relativeTime(a.last_login_at) : 'never',
                },
              ]}
            />
          </RecordSection>
          {a.memberships.length > 0 ? (
            <RecordSection label="Organizations">
              <RecordRelated
                rows={a.memberships.map((m) => ({
                  icon: 'apartment',
                  label: m.organization_name,
                  meta: m.role,
                  onClick: () => panel.push({ kind: 'organization', id: m.organization_id }),
                }))}
              />
            </RecordSection>
          ) : null}
          <RecordActions>
            <RecordOpen record={record} />
          </RecordActions>
        </RecordColumn>
      )}
    </RecordAsync>
  );
}
